import { shape, arrayOf, string } from 'prop-types';
import React, { Fragment } from 'react';
import BundleItemComponent from 'components/BundleItemComponent';
import createMetaTag from './meta-tag-factory';
import createLinkTag from './link-tag-factory';
import createTitleTag from './title-tag';
import createScriptTag from './script-tag-factory';
import {
  customComponentMapper,
  replaceEntitiesInData,
  processImageUrls,
  processJsonLd
} from './helpers';

/**
 * Metadata takes a list of metadata bundle items and outputs them to <head />
 * The bundle item type indicates what kind of metadata it is, so we can output
 * it via the appropriate tag component.
 *
 */
function Metadata(props) {
  const { items } = props;

  const MetaTagName = createMetaTag({ keyName: 'name' });
  const MetaTagProperty = createMetaTag({ keyName: 'property' });
  const TitleTag = createTitleTag();
  const LinkTag = createLinkTag();
  const ScriptTag = createScriptTag();

  // when we pass this to BundleItemComponent via components prop, it's made
  // available to customComponentMapper as the components argument.
  const COMPONENT_VARIANTS = {
    title: TitleTag,
    default: MetaTagName,
    opengraph: MetaTagProperty,
    facebook: MetaTagProperty,
    twitter: MetaTagName,
    flipboard: MetaTagName,
    links: LinkTag,
    scripts: ScriptTag,
    robots: MetaTagName,

    // intended for custom metadata that is suppressed from tangible output
    hidden: () => null
  };

  return (
    <Fragment>
      {items.map(item => {
        // CLEANUP: this is not ideal; we should not have to worry about
        // processing data at this point.
        let processedItem = replaceEntitiesInData(item);

        // update image URLs to use cloudinary URLs instead of storage URLs and
        // be optimized for each use case.
        processedItem = processImageUrls(processedItem);

        if (
          processedItem.variant === 'scripts' &&
          processedItem.data &&
          processedItem.data.name === 'application/ld+json'
        ) {
          processedItem = processJsonLd(processedItem);
        }

        return (
          <BundleItemComponent
            key={processedItem.id}
            item={processedItem}
            customComponentMapper={customComponentMapper}
            components={COMPONENT_VARIANTS}
          />
        );
      })}
    </Fragment>
  );
}

Metadata.propTypes = {
  items: arrayOf(
    shape({
      id: string.isRequired,
      variant: string.isRequired,
      data: shape({
        name: string.isRequired
      })
    })
  )
};

Metadata.defaultProps = {
  items: []
};

export default Metadata;
